import { useMemo } from "react"
import { NO_VALUE, count, money, safeDivide } from "../../../performanceSnapshot"
import { networkLabel } from "src/shared/enums/networkColors"

/*
    CPC & CPM By Network (MPN-1530 §3.3).

    The blended total row recomputes CPC and CPM off the summed spend, clicks and
    impressions. It is deliberately NOT the average of the per-network rates, which the
    ticket calls out: averaging treats a network with eleven clicks and one with eleven
    thousand as equally informative about what a click cost, and the answer it gives is
    not a price anyone paid.

    A network that spent nothing in the window still gets a row, so the table answers
    "what did Snapchat do this month" with a zero rather than by omitting Snapchat and
    leaving the reader to wonder whether it is off or just missing.
*/

type Row = {
  network: string
  spend: number
  clicks: number
  impressions: number
}

const num = (v: unknown) => {
  const n = typeof v === "number" ? v : Number(v ?? 0)
  return Number.isFinite(n) ? n : 0
}

export function costByNetwork(costRows: any[] | undefined): Row[] {
  const byNetwork = new Map<string, Row>()
  for (const row of costRows ?? []) {
    const network = row?.reporting_network
    if (!network) continue
    const acc = byNetwork.get(network) ?? { network, spend: 0, clicks: 0, impressions: 0 }
    acc.spend += num(row.spend_total)
    acc.clicks += num(row.clicks_total)
    acc.impressions += num(row.impression_total)
    byNetwork.set(network, acc)
  }
  // Server order is by the network config's sort_order; Map preserves insertion, so
  // the table reads in the same order as every other by-network chart on the page.
  return [...byNetwork.values()]
}

export default function CostByNetworkTable({ data }: { data: any }) {
  const { rows, totals } = useMemo(() => {
    const rows = costByNetwork(data?.["performance-by-day"])
    return {
      rows,
      totals: rows.reduce(
        (acc, r) => ({
          spend: acc.spend + r.spend,
          clicks: acc.clicks + r.clicks,
          impressions: acc.impressions + r.impressions,
        }),
        { spend: 0, clicks: 0, impressions: 0 },
      ),
    }
  }, [data])

  if (rows.length === 0) {
    return <p className="text-muted fs-11 mb-0">No network spend in this date range.</p>
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm mon-costtable mb-0">
        <thead>
          <tr>
            <th>Network</th>
            <th className="text-end">Spend</th>
            <th className="text-end">Clicks</th>
            <th className="text-end">Impressions</th>
            <th className="text-end">CPC</th>
            <th className="text-end">CPM</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.network}>
              <td>{networkLabel(row.network)}</td>
              <td className="text-end">{money(row.spend)}</td>
              <td className="text-end">{count(row.clicks)}</td>
              <td className="text-end">{count(row.impressions)}</td>
              <td className="text-end">{rate(row.spend, row.clicks)}</td>
              <td className="text-end">{rate(row.spend * 1000, row.impressions)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="mon-costtable__total">
            <td>Blended total</td>
            <td className="text-end">{money(totals.spend)}</td>
            <td className="text-end">{count(totals.clicks)}</td>
            <td className="text-end">{count(totals.impressions)}</td>
            {/* Off the totals, not an average of the rows above. */}
            <td className="text-end">{rate(totals.spend, totals.clicks)}</td>
            <td className="text-end">{rate(totals.spend * 1000, totals.impressions)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function rate(numerator: number, denominator: number): string {
  const value = safeDivide(numerator, denominator)
  return value === null ? NO_VALUE : money(value)
}
